/**
 * Sage customer-mapping editor and broker salesperson-code editor.
 *
 * Both are foreign-key pickers (search-select / dropdown), never numeric
 * inputs, per the UI standard. Shared by the onboarding wizard and the partner
 * detail Profile tab. Each holds a local draft set and calls back to the host
 * to persist via the PUT-replace endpoints.
 */

import { useMemo, useState } from 'react';
import { Plus, Trash2, Search, Loader2 } from 'lucide-react';
import {
  useSageCustomerSearch,
  useConnectSalespersonCodes,
  sageCustomerLabel,
  salespersonCodeLabel,
  type SageCustomerRef,
  type SalespersonCodeRef,
} from '@/lib/useConnect';
import { inputCls, labelCls } from './connectShared';

// ---------- Sage customer mappings ----------------------------------------

export function CustomerMappingEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: SageCustomerRef[];
  onChange: (next: SageCustomerRef[]) => void;
  disabled?: boolean;
}) {
  const [q, setQ] = useState('');
  const search = useSageCustomerSearch(q);

  const selectedNos = useMemo(() => new Set(value.map((c) => c.customer_no)), [value]);

  const addCustomer = (customerNo: string, customerName?: string) => {
    if (selectedNos.has(customerNo)) return;
    const next: SageCustomerRef[] = [
      ...value,
      { division_no: '', customer_no: customerNo, is_primary: value.length === 0, name: customerName ?? null },
    ];
    onChange(next);
    setQ('');
  };

  const removeCustomer = (customerNo: string) => {
    const remaining = value.filter((c) => c.customer_no !== customerNo);
    // Ensure exactly one primary survives.
    if (remaining.length > 0 && !remaining.some((c) => c.is_primary)) {
      remaining[0] = { ...remaining[0]!, is_primary: true };
    }
    onChange(remaining);
  };

  const setPrimary = (customerNo: string) => {
    onChange(value.map((c) => ({ ...c, is_primary: c.customer_no === customerNo })));
  };

  return (
    <div className="space-y-3">
      {value.length > 0 ? (
        <ul className="divide-y divide-amp-line rounded border border-amp-line dark:divide-amp-dark-line dark:border-amp-dark-line">
          {value.map((c) => (
            <li key={c.customer_no} className="flex items-center justify-between gap-3 px-3 py-2">
              <label className="inline-flex items-center gap-2 text-sm text-amp-ink dark:text-amp-dark-ink">
                <input
                  type="radio"
                  name="primary-customer"
                  checked={c.is_primary}
                  disabled={disabled}
                  onChange={() => setPrimary(c.customer_no)}
                />
                <span className="font-mono text-xs">{sageCustomerLabel(c)}</span>
                {c.is_primary && (
                  <span className="text-xs text-amp-olive dark:text-amp-olive-dark">Primary</span>
                )}
              </label>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeCustomer(c.customer_no)}
                  aria-label={`Remove ${c.customer_no}`}
                  className="text-amp-ink-muted hover:text-red-700 dark:text-amp-dark-ink-muted"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No Sage customers mapped yet.</p>
      )}

      {!disabled && (
        <div>
          <label className={labelCls}>Add a Sage customer</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-amp-ink-muted dark:text-amp-dark-ink-muted" aria-hidden />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Sage customers by name or city..."
              className={`${inputCls} pl-7`}
            />
          </div>
          {q.trim().length >= 2 && (
            <div className="mt-1 max-h-56 overflow-auto rounded border border-amp-line dark:border-amp-dark-line">
              {search.isLoading ? (
                <p className="inline-flex items-center gap-2 px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                </p>
              ) : search.customers.length === 0 ? (
                <p className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No matches.</p>
              ) : (
                search.customers.map((row) => (
                  <button
                    key={row.CustomerNo}
                    type="button"
                    onClick={() => addCustomer(row.CustomerNo, row.CustomerName)}
                    disabled={selectedNos.has(row.CustomerNo)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-amp-ink hover:bg-amp-cream-warm disabled:opacity-50 dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{row.CustomerName}</span>
                      <span className="block truncate text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        {row.CustomerNo} · {row.City}{row.State ? `, ${row.State}` : ''}
                      </span>
                    </span>
                    <Plus className="h-3.5 w-3.5 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Broker salesperson codes --------------------------------------

export function SalespersonCodeEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: SalespersonCodeRef[];
  onChange: (next: SalespersonCodeRef[]) => void;
  disabled?: boolean;
}) {
  const { codes, isLoading } = useConnectSalespersonCodes();
  const [toAdd, setToAdd] = useState('');

  const selected = useMemo(() => new Set(value.map((c) => c.salesperson_no)), [value]);
  const available = useMemo(
    () => codes.filter((c) => !selected.has(c.salesperson_no)),
    [codes, selected],
  );

  const add = () => {
    if (!toAdd || selected.has(toAdd)) return;
    // Carry the picked code's name so the newly added row shows "CODE - Name"
    // immediately, before any refetch.
    const picked = codes.find((c) => c.salesperson_no === toAdd);
    onChange([
      ...value,
      { division_no: '', salesperson_no: toAdd, name: picked?.name ?? null, commission_rate: null },
    ]);
    setToAdd('');
  };

  const remove = (no: string) => onChange(value.filter((c) => c.salesperson_no !== no));

  const setRate = (no: string, raw: string) => {
    const parsed = raw === '' ? null : Number(raw);
    onChange(
      value.map((c) =>
        c.salesperson_no === no
          ? { ...c, commission_rate: parsed != null && Number.isFinite(parsed) ? parsed : null }
          : c,
      ),
    );
  };

  return (
    <div className="space-y-3">
      {value.length > 0 ? (
        <ul className="divide-y divide-amp-line rounded border border-amp-line dark:divide-amp-dark-line dark:border-amp-dark-line">
          {value.map((c) => (
            <li key={c.salesperson_no} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="font-mono text-sm text-amp-ink dark:text-amp-dark-ink">{salespersonCodeLabel(c)}</span>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Commission
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    disabled={disabled}
                    value={c.commission_rate ?? ''}
                    onChange={(e) => setRate(c.salesperson_no, e.target.value)}
                    className="w-20 border border-amp-line rounded px-2 py-1 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                  />
                  %
                </label>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(c.salesperson_no)}
                    aria-label={`Remove ${c.salesperson_no}`}
                    className="text-amp-ink-muted hover:text-red-700 dark:text-amp-dark-ink-muted"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No salesperson codes mapped yet.</p>
      )}

      {!disabled && (
        <div>
          <label className={labelCls}>Add a salesperson code</label>
          <div className="flex gap-2">
            <select
              value={toAdd}
              onChange={(e) => setToAdd(e.target.value)}
              disabled={isLoading}
              className={inputCls}
            >
              <option value="">{isLoading ? 'Loading codes...' : 'Select a code...'}</option>
              {available.map((c) => (
                <option key={c.salesperson_no} value={c.salesperson_no}>
                  {salespersonCodeLabel(c)} ({c.customer_count} customers, {c.invoice_count} invoices)
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={add}
              disabled={!toAdd}
              className="inline-flex items-center gap-1 shrink-0 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
